/**
 * Service for fetching and managing BEO ATLAS codelists
 * Uses backend as single source of truth with smart caching
 */

import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export interface CodelistValue {
  code: string;
  description: string;
  additionalInfo?: string;
  type?: string;
  isSelected?: boolean;
}

export interface Codelist {
  id: string;
  name: string;
  description?: string;
  values: CodelistValue[];
}

interface FieldCodelistMapping {
  fieldMapping: Record<string, string>;
  totalFields: number;
  uniqueCodelists: number;
}

interface AllCodelistsResponse {
  version: string;
  timestamp: string;
  source?: string; // "atlas_db" | "atlas_live" | "fallback"
  codelists: Record<string, Codelist>;
  field_mappings: Record<string, string>;
}

// Cache for codelist data
const codelistCache: Map<string, Codelist> = new Map();
let fieldMappings: Record<string, string> = {};
let cacheVersion = '';
let initialized = false;
let initializationPromise: Promise<void> | null = null;

// Local storage keys — bump version when stored data structure changes
// v7: added D0295 (Intended Exit Customs Office, Atlas type 122) — force refetch.
// v8: added C0017 (package type, Atlas type 120) and S0615 (authorisation type,
//     Atlas type 139) field mappings — force refetch so warm caches pick them up.
const STORAGE_KEY_CODELISTS = 'beo_codelists_v8';
const STORAGE_KEY_VERSION = 'beo_codelists_version_v8';
const STORAGE_KEY_TIMESTAMP = 'beo_codelists_timestamp_v8';
const STORAGE_KEY_MAPPINGS = 'beo_field_mappings_v8';
const STORAGE_KEY_SOURCE = 'beo_codelists_source_v8';
const STORAGE_KEY_USER = 'beo_codelists_user_v8';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours (for Atlas data)

// Deferred call — never invoked at module top level to avoid import cycles.
const getCurrentUserId = (): string | null => {
  try { return useAuthStore.getState().user?.id ?? null; } catch { return null; }
};
const FALLBACK_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes (for fallback data)

// English field names to ATLAS path mapping (kept for backward compatibility)
export const ENGLISH_TO_ATLAS_PATH: Record<string, string> = {
  // Customs offices
  'Export Customs Office': 'ausfuhrzollstelleDienststellennummer',
  'Customs Office for Supplementary Declaration': 'ausfuhrZollstelleFurDieErganzende',
  'Intended Exit Customs Office': 'vorgeseheneAusgangszollstelleDienststellennummer',
  'Customs Office of Presentation': 'gestellungszollstelle',
  
  // Countries
  'Export Country': 'ausfuhrLand',
  'Germany': 'ausfuhrLand', // Special case - value mapping for Export Country
  'Destination Country': 'bestimmungsLand',
  'Israel': 'bestimmungsLand', // Special case - value mapping 
  'Exporter Country': 'kopf.ausfuhrer.land',
  'Consignee Country': 'kopf.empfanger.land',
  'Declarant Country': 'kopf.anmelder.land',
  
  // Currency and amounts
  'Invoice Currency': 'kopf.rechnungswahrung',
  'Currency': 'kopf.rechnungswahrung',
  'EUR': 'kopf.rechnungswahrung', // Special case - value mapping for currency
  
  // Transport
  'Domestic Transport Mode': 'inlandischerVerkehrszweig',
  'Transport Mode at Border': 'verkehrszweigAnDerGrenze',
  
  // Trade terms
  'Incoterms': 'lieferbedingungIncotermCode',
  'UN/LOCODE': 'lieferbedingungUnLocode',
  
  // Containers
  'Container Indicator': 'containerIndikator',
  
  // Security
  'Security Indicator': 'kopf.sicherheitIndikator',
  
  // Declaration types
  'Declaration Type': 'kopf.artderAnmeldung',
  'Export Declaration Type': 'kopf.artderAusfuhranmeldung',
  'Participant Constellation': 'kopf.beteiligtenKonstellation',
};

class CodelistService {
  /**
   * Initialize the service by loading all codelists
   * This should be called once when the app starts
   */
  async initialize(): Promise<void> {
    // Prevent multiple simultaneous initializations
    if (initializationPromise) {
      return initializationPromise;
    }
    
    if (initialized) {
      return;
    }
    
    initializationPromise = this._doInitialize();
    return initializationPromise;
  }
  
  /**
   * Force refresh all codelists from backend
   * Bypasses cache and fetches fresh data
   */
  async forceRefresh(): Promise<void> {
    console.log('Force refreshing all codelists from backend...');
    
    // Clear existing cache
    codelistCache.clear();
    fieldMappings = {};
    localStorage.removeItem(STORAGE_KEY_CODELISTS);
    localStorage.removeItem(STORAGE_KEY_VERSION);
    localStorage.removeItem(STORAGE_KEY_TIMESTAMP);
    localStorage.removeItem(STORAGE_KEY_MAPPINGS);
    localStorage.removeItem(STORAGE_KEY_SOURCE);
    localStorage.removeItem(STORAGE_KEY_USER);
    
    // Reset initialization state
    initialized = false;
    initializationPromise = null;
    
    // Re-initialize with fresh data
    await this.initialize();
  }
  
  /**
   * Check if cache is complete (has expected number of codelists)
   */
  isCacheComplete(): boolean {
    const EXPECTED_CODELIST_COUNT = 20; // We expect at least 20 codelists
    const actualCount = codelistCache.size;
    
    if (actualCount < EXPECTED_CODELIST_COUNT) {
      console.warn(`Codelist cache incomplete: ${actualCount} of ${EXPECTED_CODELIST_COUNT}+ expected`);
      return false;
    }
    
    return true;
  }
  
  /**
   * Get current cache size for debugging
   */
  getCacheSize(): number {
    return codelistCache.size;
  }
  
  /**
   * Get all cached codelist IDs for debugging
   */
  getCachedCodelistIds(): string[] {
    return Array.from(codelistCache.keys());
  }

  /** True only when the cached codelists were built for the current user. */
  isCacheForCurrentUser(): boolean {
    return localStorage.getItem(STORAGE_KEY_USER) === (getCurrentUserId() ?? '__none__');
  }

  private async _doInitialize(): Promise<void> {
    try {
      // Try to load from localStorage first
      const cachedData = this.loadFromLocalStorage();
      
      if (cachedData && this.isCacheForCurrentUser()) {
        // Check if cache is still valid
        const cachedTimestamp = localStorage.getItem(STORAGE_KEY_TIMESTAMP);
        const cachedSource = localStorage.getItem(STORAGE_KEY_SOURCE) || 'unknown';
        const cacheAge = cachedTimestamp ? Date.now() - new Date(cachedTimestamp).getTime() : Infinity;

        // All data comes from Atlas API — use standard 24h TTL
        if (cacheAge < CACHE_DURATION_MS && this.isCacheComplete()) {
          console.log(`Using cached codelists from localStorage (${codelistCache.size} codelists, source: ${cachedSource})`);
          initialized = true;
          return;
        } else if (!this.isCacheComplete()) {
          console.warn(`Cache incomplete (${codelistCache.size} codelists), fetching fresh data...`);
        }
      } else if (cachedData) {
        console.log('Codelist cache belongs to a different user — discarding and refetching.');
        this.clearCache();
      }
      
      // Fetch fresh data from backend
      await this.fetchAllCodelists();
      initialized = true;
    } catch (error) {
      console.error('Failed to initialize codelist service:', error);
      // If we have cached data (real Atlas data persisted earlier), use it even
      // if expired. Otherwise leave codelists empty — never inject hardcoded
      // values; dropdown options must always originate from Atlas via the backend.
      if (codelistCache.size > 0) {
        console.warn('Using expired cached codelists');
      }
      initialized = true;
    } finally {
      initializationPromise = null;
    }
  }
  
  /**
   * Fetch all codelists from backend at once
   */
  private async fetchAllCodelists(): Promise<void> {
    try {
      console.log('Fetching all codelists from backend...');
      const response = await axios.get<AllCodelistsResponse>(
        `${API_BASE_URL}/api/v1/codelists/all`
      );
      
      if (response.data) {
        // Clear existing cache
        codelistCache.clear();
        
        // Store all codelists in cache
        Object.entries(response.data.codelists).forEach(([id, codelist]) => {
          codelistCache.set(id, codelist);
        });
        
        // Store field mappings
        fieldMappings = response.data.field_mappings || {};
        cacheVersion = response.data.version;
        const dataSource = response.data.source || 'unknown';

        // Save to localStorage
        this.saveToLocalStorage(response.data);

        console.log(`Loaded ${codelistCache.size} codelists with version ${cacheVersion} (source: ${dataSource})`);
        if (dataSource === 'fallback') {
          console.warn('⚠️ Codelist data is from hardcoded fallback — Atlas API not yet available. Will retry in 5 minutes.');
        }
      }
    } catch (error) {
      console.error('Failed to fetch all codelists:', error);
      throw error;
    }
  }
  
  /**
   * Save codelists to localStorage
   */
  private saveToLocalStorage(data: AllCodelistsResponse): void {
    try {
      const codelistsArray = Array.from(codelistCache.entries());
      localStorage.setItem(STORAGE_KEY_CODELISTS, JSON.stringify(codelistsArray));
      localStorage.setItem(STORAGE_KEY_VERSION, data.version);
      localStorage.setItem(STORAGE_KEY_TIMESTAMP, data.timestamp || new Date().toISOString());
      localStorage.setItem(STORAGE_KEY_MAPPINGS, JSON.stringify(data.field_mappings));
      localStorage.setItem(STORAGE_KEY_SOURCE, data.source || 'unknown');
      localStorage.setItem(STORAGE_KEY_USER, getCurrentUserId() ?? '__none__');
    } catch (error) {
      console.error('Failed to save codelists to localStorage:', error);
    }
  }
  
  /**
   * Load codelists from localStorage
   */
  private loadFromLocalStorage(): boolean {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_CODELISTS);
      const storedVersion = localStorage.getItem(STORAGE_KEY_VERSION);
      const storedMappings = localStorage.getItem(STORAGE_KEY_MAPPINGS);
      
      if (stored) {
        const codelistsArray: [string, Codelist][] = JSON.parse(stored);
        codelistCache.clear();
        codelistsArray.forEach(([id, codelist]) => {
          codelistCache.set(id, codelist);
        });
        
        if (storedMappings) {
          fieldMappings = JSON.parse(storedMappings);
        }
        
        cacheVersion = storedVersion || '';
        return true;
      }
    } catch (error) {
      console.error('Failed to load codelists from localStorage:', error);
    }
    return false;
  }
  
  /**
   * Get a single codelist by ID
   */
  async getCodelist(codelistId: string): Promise<Codelist | null> {
    // Ensure service is initialized
    if (!initialized) {
      await this.initialize();
    }
    
    // Return from cache
    return codelistCache.get(codelistId) || null;
  }
  
  /**
   * Get multiple codelists in a single request
   * Since we load all codelists at initialization, this just returns from cache
   */
  async getMultipleCodelists(codelistIds: string[]): Promise<Record<string, Codelist>> {
    // Ensure service is initialized
    if (!initialized) {
      await this.initialize();
    }
    
    const result: Record<string, Codelist> = {};
    
    // Get from cache
    codelistIds.forEach(id => {
      const codelist = codelistCache.get(id);
      if (codelist) {
        result[id] = codelist;
      }
    });
    
    return result;
  }
  
  /**
   * Get the codelist ID for a specific field path
   */
  getCodelistIdForField(fieldPath: string): string | null {
    // Normalise the position array index (position[N].x -> position.x) so a
    // single mapping covers every line item.
    const normalizedPath = fieldPath.replace(/^position\[\d+\]\./, 'position.');

    // First check field mappings from backend
    if (fieldMappings[fieldPath]) {
      return fieldMappings[fieldPath];
    }
    if (normalizedPath !== fieldPath && fieldMappings[normalizedPath]) {
      return fieldMappings[normalizedPath];
    }

    // Then check English to ATLAS path mapping for backward compatibility
    const atlasPath = ENGLISH_TO_ATLAS_PATH[fieldPath];
    if (atlasPath && fieldMappings[atlasPath]) {
      return fieldMappings[atlasPath];
    }
    
    // Complete field to codelist mappings (synced with backend FIELD_TO_CODELIST_MAP)
    const fieldToCodelist: Record<string, string> = {
      // Header fields - Declaration types
      'kopf.artderAnmeldung': 'C0231',
      'kopf.artderAusfuhranmeldung': 'A0121',
      'kopf.beteiligtenKonstellation': 'A0127',  // A0127 from Atlas type 106
      'kopf.angabenGeschaftsvorgangArt': 'A1150',  // A1150 from Atlas type 105 (Art des Geschäftsvorgangs)
      'angabenGeschaftsvorgangArt': 'A1150',
      'kopf.sicherheit': 'C0217',                // C0217 from Atlas type 108
      'kopf.sicherheitIndikator': 'C0217',
      'kopf.besondereUmstande': 'C0025',
      'kopf.beforderungskostenZahlungsart': 'C0024',

      // Currency
      'kopf.rechnungswahrung': 'C0352',

      // Party identification fields
      'kopf.ausfuhrer.identifikationsart': 'A1150',  // A1150 from Atlas type 105
      'kopf.empfanger.identifikationsart': 'A1150',
      'kopf.anmelder.identifikationsart': 'A1150',
      'kopf.vertreter.identifikationsart': 'A1150',
      'kopf.anmelder.beauftragterStellung': 'S0347',  // S0347 from Atlas type 142
      'kopf.vertreter.beauftragterStellung': 'S0347',
      'kopf.ausfuhrer.tinCheck': 'S0326',   // S0326 from Atlas type 141
      'kopf.anmelder.tinCheck': 'S0326',
      'kopf.vertreter.tinCheck': 'S0326',

      // Customs offices
      'gestellungszollstelle': 'D0293',
      'kopf.gestellungszollstelle': 'D0293',
      'ausfuhrzollstelleDienststellennummer': 'D0293',
      'kopf.ausfuhrzollstelleDienststellennummer': 'D0293',
      'ausfuhrZollstelleFurDieErganzende': 'D0294',
      'kopf.ausfuhrZollstelleFurDieErganzende': 'D0294',
      'erganzungszollstelleAusfuhrDienststellennummer': 'D0294',
      'kopf.erganzungszollstelleAusfuhrDienststellennummer': 'D0294',
      'vorgeseheneAusgangszollstelleDienststellennummer': 'D0295',  // Intended Exit Customs Office (Atlas type 122)
      'kopf.vorgeseheneAusgangszollstelleDienststellennummer': 'D0295',
      'dienststellennummer': 'D0293',
      'kopf.dienststellennummer': 'D0293',

      // Countries
      'kopf.ausfuhrer.land': 'C0248',
      'kopf.empfanger.land': 'C0248',
      'kopf.anmelder.land': 'C0248',
      'kopf.vertreter.land': 'C0248',
      'kopf.subunternehmer.land': 'C0248',
      'kopf.versender.land': 'C0248',
      'kopf.aussenAusfuhrer.land': 'C0248',
      'ausfuhrLand': 'C0010',
      'kopf.ausfuhrland': 'C0010',
      'bestimmungsLand': 'C0008',
      'kopf.bestimmungsLand': 'C0008',
      'lieferbedingungLand': 'C0248',
      'kopf.lieferbedingungLand': 'C0248',

      // Transport
      'inlandischerVerkehrszweig': 'C0018',
      'kopf.inlandischerVerkehrszweig': 'C0018',
      'verkehrszweigAnDerGrenze': 'C0019',
      'kopf.verkehrszweigAnDerGrenze': 'C0019',
      'containerIndikator': 'S0027',
      'kopf.containerIndikator': 'S0027',
      'kopf.beforderungsmittelderGrenzeArt': 'C0219',       // C0219 from Atlas type 111
      'kopf.beforderungsmittelderGrenzeStaatszugehorigkeit': 'C0165',  // C0165 from Atlas type 124
      'kopf.beforderungsmittelBeimAbgang.staatszugehorigkeit': 'C0165',
      'kopf.artEinheitsBeforderungsmittels': 'C0249',

      // Trade terms and locations
      'lieferbedingungIncotermCode': 'C0090',
      'kopf.lieferbedingungIncotermCode': 'C0090',
      'lieferbedingungUnLocode': 'C0214',
      'kopf.lieferbedingungUnLocode': 'C0214',
      'kopf.ladeort.code': 'C0214',
      'kopf.warenortUnLocode': 'C0214',
      'kopf.gestellungVerpackung': 'C0091',
      'kopf.gestellungVerpackungArt': 'C0091',

      // Location fields (S0347 from Atlas type 142, S0326 from Atlas type 141)
      'kopf.warenortArtdesOrtes': 'S0347',
      'kopf.warenortArtDerOrtsbestimmung': 'S0326',

      // Transport fields with array notation (C0219 from type 111, C0165 from type 124)
      'kopf.beforderungsmittelBeimAbgang[0].artderIdentifikation': 'C0219',
      'kopf.beforderungsmittelBeimAbgang[0].staatszugehorigkeit': 'C0165',
      'kopf.beforderungsroute.ausgewahlteLander': 'C0248',

      // Position-level fields
      'position.ursprungsland': 'C0248',
      // Line-item package type (Verpackungsart) — Atlas codelist type 120
      'position.verpackung[0].artderVerpackung': 'C0017',

      // Header authorisation type (Art der Bewilligung) — Atlas codelist type 139
      'kopf.bewilligung[0].art': 'S0615',
    };

    return fieldToCodelist[fieldPath] || fieldToCodelist[normalizedPath] || null;
  }
  
  /**
   * Get field to codelist mapping
   */
  async getFieldCodelistMapping(): Promise<FieldCodelistMapping> {
    // Ensure service is initialized
    if (!initialized) {
      await this.initialize();
    }
    
    return {
      fieldMapping: fieldMappings,
      totalFields: Object.keys(fieldMappings).length,
      uniqueCodelists: new Set(Object.values(fieldMappings)).size
    };
  }
  
  /**
   * Clear the cache
   */
  clearCache(): void {
    codelistCache.clear();
    initialized = false;
    localStorage.removeItem(STORAGE_KEY_CODELISTS);
    localStorage.removeItem(STORAGE_KEY_VERSION);
    localStorage.removeItem(STORAGE_KEY_TIMESTAMP);
    localStorage.removeItem(STORAGE_KEY_MAPPINGS);
    localStorage.removeItem(STORAGE_KEY_USER);
  }
  
  /**
   * Force refresh all codelists from backend
   */
  async refresh(): Promise<void> {
    this.clearCache();
    await this.initialize();
  }
  
  /**
   * Preload codelists for a set of fields
   */
  async preloadCodelistsForFields(fieldPaths: string[]): Promise<void> {
    // Ensure service is initialized
    if (!initialized) {
      await this.initialize();
    }
    // Since all codelists are loaded at initialization, nothing to do here
  }
  
  /**
   * Convert extracted value to codelist code
   * Handles cases where OCR extracts the description instead of code
   */
  convertValueToCode(value: string, codelistId: string): string {
    if (!value) return value;
    
    // Common conversions for country names to codes
    const countryNameToCode: Record<string, string> = {
      'germany': 'DE',
      'deutschland': 'DE',
      'france': 'FR',
      'frankreich': 'FR',
      'italy': 'IT',
      'italien': 'IT',
      'italia': 'IT',
      'spain': 'ES',
      'spanien': 'ES',
      'españa': 'ES',
      'netherlands': 'NL',
      'niederlande': 'NL',
      'belgium': 'BE',
      'belgien': 'BE',
      'austria': 'AT',
      'österreich': 'AT',
      'poland': 'PL',
      'polen': 'PL',
      'czech republic': 'CZ',
      'tschechien': 'CZ',
      'switzerland': 'CH',
      'schweiz': 'CH',
      'united kingdom': 'GB',
      'vereinigtes königreich': 'GB',
      'uk': 'GB',
      'united states': 'US',
      'vereinigte staaten': 'US',
      'usa': 'US',
      'china': 'CN',
      'japan': 'JP',
      'india': 'IN',
      'indien': 'IN',
    };
    
    // Common currency names to codes
    const currencyNameToCode: Record<string, string> = {
      'euro': 'EUR',
      'dollar': 'USD',
      'us dollar': 'USD',
      'pound': 'GBP',
      'british pound': 'GBP',
      'pfund': 'GBP',
      'franc': 'CHF',
      'franken': 'CHF',
      'swiss franc': 'CHF',
      'yen': 'JPY',
      'yuan': 'CNY',
    };
    
    const valueLower = value.toLowerCase().trim();
    
    // Check if it's already a valid code (2-3 uppercase letters)
    if (/^[A-Z]{2,3}$/.test(value)) {
      return value;
    }
    
    // Try to convert based on codelist type
    if (codelistId === 'C0248' || codelistId === 'C0010' || codelistId === 'C0008') {
      // Country codelists
      return countryNameToCode[valueLower] || value.toUpperCase().substring(0, 2);
    }
    
    if (codelistId === 'C0352') {
      // Currency codelist
      return currencyNameToCode[valueLower] || value.toUpperCase().substring(0, 3);
    }
    
    // For container indicator, convert yes/no to 1/0
    if (codelistId === 'S0027') {
      if (valueLower === 'yes' || valueLower === 'ja' || valueLower === '1') return '1';
      if (valueLower === 'no' || valueLower === 'nein' || valueLower === '0') return '0';
    }
    
    // Default: return uppercase version
    return value.toUpperCase();
  }
  
  /**
   * Find best matching codelist value for extracted text
   */
  findBestMatch(extractedValue: string, codelist: Codelist): CodelistValue | null {
    if (!extractedValue || !codelist || !Array.isArray(codelist.values)) return null;

    const valueLower = extractedValue.toLowerCase().trim();
    
    // First try exact code match
    const exactMatch = codelist.values.find(v => v.code.toLowerCase() === valueLower);
    if (exactMatch) return exactMatch;
    
    // Try to convert and match
    const convertedCode = this.convertValueToCode(extractedValue, codelist.id);
    const convertedMatch = codelist.values.find(v => v.code === convertedCode);
    if (convertedMatch) return convertedMatch;
    
    // Try partial description match
    const descriptionMatch = codelist.values.find(v => 
      v.description.toLowerCase().includes(valueLower) ||
      valueLower.includes(v.description.toLowerCase())
    );
    if (descriptionMatch) return descriptionMatch;
    
    return null;
  }
}

// Export singleton instance
const codelistService = new CodelistService();
export default codelistService;

// Isolate codelist defaults per user: drop the cache whenever the signed-in
// user changes (login / logout / switch). One-directional dep on the auth store.
let __lastCodelistUserId: string | null = useAuthStore.getState().user?.id ?? null;
useAuthStore.subscribe((state) => {
  const id = state.user?.id ?? null;
  if (id !== __lastCodelistUserId) {
    __lastCodelistUserId = id;
    codelistService.clearCache();
  }
});